/* eslint-disable guard-for-in */
import {
    reactive,
} from 'vue';

import moment from 'moment';

// https://hatscripts.github.io/circle-flags/flags/{{ lang }}.svg
import flag_ru from './assets/locales/ic_flags_ru.svg';
import flag_en from './assets/locales/ic_flags_gb.svg';

// const PLURALS_ZERO = "zero";
const PLURALS_ONE = 'one';
// const PLURALS_TWO = "two";
const PLURALS_FEW = 'few';
const PLURALS_MANY = 'many';
const PLURALS_OTHER = 'other';

const options = reactive({
    lang: null,
});

let clang = localStorage.getItem('lang');
if (clang == null) {
    clang = navigator.language;
    const index = clang.indexOf('-');
    if (index > 0) {
        clang = clang.substring(0, index);
    }
}
moment.locale(clang);
options.lang = clang;

class ContextProvider {
    constructor(context) {
        this.context = context;
    }

    getKeys() {
        const keys = [];
        this.context.keys().forEach((key) => {
            keys.push(key);
        });
        return keys;
    }

    async loadModule(key) {
        const module = this.context(key);
        const uri = module?.default || module;
        const data = await fetch(uri);
        if (data.ok) {
            const json = await data.json();
            return json;
        }
        return {};
    }
}

export class Localization {
    constructor() {
        this.providers = {};
        this.values = reactive({
            lang: options.clang,
            dictionary: {},
        });
    }

    install(app) {
        app.config.globalProperties.$tr = (phrase) => this.tr(phrase);
        app.config.globalProperties.$plural = (phrase, number) => this.plural(phrase, number);
    }

    async installContext(context) {
        return this.installProvider(new ContextProvider(context));
    }

    async installProvider(provider) {
        const keys = await provider.getKeys();
        const installing = [];
        for (let i = 0; i < keys.length; i += 1) {
            const key = keys[i];
            let name = key;
            let index = name.lastIndexOf('/');
            if (index >= 0) {
                name = name.substring(index + 1);
            }
            index = name.lastIndexOf('.');
            if (index >= 0) {
                name = name.substring(0, index);
            }
            let modules = this.providers[name];
            if (modules == null) {
                modules = [];
                this.providers[name] = modules;
            }
            modules.push({
                provider,
                key,
            });
            if (this.values.lang === name) {
                installing.push(provider.loadModule(key));
            }
        }
        if (installing.length > 0) {
            const name = this.values.lang;
            const dicts = await Promise.all(installing);
            if (this.values.lang === name) {
                for (let i = 0; i < dicts.length; i += 1) {
                    const dict = dicts[i];
                    for (const phrase in dict) {
                        this.values.dictionary[phrase] = dict[phrase];
                    }
                }
            }
        }
    }

    async activate(lang) {
        if (options.lang !== lang) {
            options.lang = lang;
            await this.switch(lang);
            moment.locale(lang);
            localStorage.setItem('lang', lang);
        }
    }

    async switch(lang) {
        if (this.values.lang !== lang) {
            this.values.lang = lang;
            const modules = this.providers[lang];
            if (modules != null) {
                const promises = [];
                for (let i = 0; i < modules.length; i += 1) {
                    const item = modules[i];
                    promises.push(item.provider.loadModule(item.key));
                }
                const dicts = await Promise.all(promises);
                const ndict = {};
                for (let i = 0; i < dicts.length; i += 1) {
                    const dict = dicts[i];
                    for (const phrase in dict) {
                        const value = dict[phrase];
                        ndict[phrase] = value;
                        this.values.dictionary[phrase] = value;
                    }
                }
                for (const phrase in this.values.dictionary) {
                    if (ndict[phrase] === undefined) {
                        delete this.values.dictionary[phrase];
                    }
                }
            } else {
                this.values.dictionary = {};
            }
        }
    }

    tr(phrase, def) {
        if (phrase == null) {
            return undefined;
        }
        if (this.values.lang !== options.lang) {
            this.switch(options.lang);
        }
        const dictionary = this.values.dictionary;
        const value = dictionary[phrase];
        if (value != null) {
            return value;
        }
        if ((this.values.lang != null) && (this.values.lang !== 'ru') && (def === undefined)) {
            if (Object.keys(dictionary).length > 0) {
                // eslint-disable-next-line no-console
                console.warn(`localization massage not found "${phrase}" in lang "${this.values.lang}"`);
            }
        }
        return def === undefined ? phrase : def;
    }

    plural(phrase, number) {
        if (phrase == null) {
            return undefined;
        }
        if (this.values.lang !== options.lang) {
            this.switch(options.lang);
        }
        const dictionary = this.values.dictionary;
        const value = dictionary[phrase];
        if (value != null) {
            const plural = this.pluralsType(number);
            const s = value[plural];
            if (s != null) {
                return s;
            }
            return value[PLURALS_OTHER];
        }
        // if ((this.values.lang != undefined) && (this.values.lang != "ru")) {
        //     console.warn(`localization massage not found "${phrase}" in lang "${this.values.lang}"`);
        // }
        return phrase;
    }

    pluralsType(number) {
        if (this.values.lang === 'ru') {
            let n = number % 100;
            if (n > 20) {
                n %= 10;
            }
            switch (n) {
            case 1:
                return PLURALS_ONE;
            case 2:
            case 3:
            case 4:
                return PLURALS_FEW;
            default:
                return PLURALS_MANY;
            }
        } else {
            if (number === 1) {
                return PLURALS_ONE;
            }
            return PLURALS_MANY;
        }
    }

    lang() {
        return this.values.lang;
    }

    locales() {
        const locales = {};
        for (const locale in this.providers) {
            switch (locale) {
            case 'ru':
                locales[locale] = {
                    name: 'Русский',
                    icon: flag_ru,
                };
                break;
            case 'en':
                locales[locale] = {
                    name: 'English',
                    icon: flag_en,
                };
                break;
            default:
                // eslint-disable-next-line no-console
                console.warn(`undefined locale ${locale}`);
                break;
            }
        }
        return locales;
    }
}

export const localization = new Localization();

export function installContext(context) {
    localization.installContext(context);
}
